'use client';
import React, { useState, useEffect } from 'react';
import { Drawer, Box, Typography, IconButton, CircularProgress, Stack } from '@mui/material';

import { KeyboardArrowRight } from '@mui/icons-material';
import {formatToOMNDateTime} from '../../utils/formatDate';
import { useGetTimelineDataHandler } from './commonDataHandlers/getTimelineDataHandler';

const DocumentTimeline = ({ documentType, docId, isPurchase, isOpen, onClose }) => {
  const { timelineData, isLoading, lastFetchTime } = useGetTimelineDataHandler(documentType, docId, isOpen, isPurchase);

  return (
    <>
      {/* Right Drawer */}
      <Drawer
        id="timeline-drawer"
        anchor="right"
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            width: 330,
            p: 2,
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" component="h2" fontWeight={600}>
            Document Timeline
          </Typography>
          <IconButton onClick={onClose} size="small">
            <KeyboardArrowRight />
          </IconButton>
        </Box>

        {/* Timeline Content */}
        <Box
          sx={{
            position: 'relative',
            minHeight: 'calc(100vh - 200px)',
            overflow: 'auto',
            pb: 8,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            py: 2,
          }}
        >
          {timelineData && timelineData.length > 0 ? (
            <Box sx={{ position: 'relative' }}>
              {/* Vertical Timeline Line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 10,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  backgroundColor: 'divider',
                  zIndex: 1,
                }}
              />

              {/* Timeline Events */}
              {timelineData.map((event) => (
                <Box
                  key={event.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 3,
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
                  {/* Timeline Dot */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 11,
                      transform: 'translateX(-50%)',
                      zIndex: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: 'grey.400',
                        border: '2px solid',
                        borderColor: 'grey.300',
                      }}
                    />
                  </Box>

                  {/* Event Card */}
                  <Box
                    sx={{
                      ml: 4,
                      borderRadius: 2,
                      width: 300,
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {event.message}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">
                      {formatToOMNDateTime(event.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {isLoading ? (
                <Stack direction="column" alignItems="center" spacing={2}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Loading timeline...
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No timeline data available
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Sticky Footer */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            width: 330,
            backgroundColor: 'background.paper',
            p: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 10,
            boxShadow: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Last updated: {formatToOMNDateTime(lastFetchTime)}
          </Typography>
        </Box>
      </Drawer>
    </>
  );
};

export default DocumentTimeline;
